import { GrowthRateBasisType, ReferenceData } from '@xplan-pension-switching/common';
import { IressForm, IressFormField, IressInput, IressText } from '@iress-oss/ids-components';
import { useGrowthRateTableValues } from '../UseGrowthRateTableReset/UseGrowthRateTableValues';
import { FormEvent, useMemo } from 'react';
import {
    calculateNominalFromInflationAdjustedRateFormatted,
    formatTwoDecimalPlaces,
    twoDecimalPlaceRegex,
} from '../../helpers';
import { useAtLeastOneGreaterThanZero } from '../UseAtLeastOneGreaterThanZero/UseAtLeastOneGreaterThanZero';
import { getSuffixFromGrowthRateBasisType } from '../../helpers/GetSuffixFromGrowthRateBasisType/GetSuffixFromGrowthRateBasisType';

const allRatesZeroErrorMessage = 'At least one rate must be greater than 0';
export const useInflationAdjustedRows = ({
    growthRateLow,
    growthRateMid,
    growthRateHigh,
    selectedGrowthRateBasisType,
    referenceData,
    defaultGrowthRateBasisType,
}: {
    growthRateLow?: number;
    growthRateMid?: number;
    growthRateHigh?: number;
    referenceData: ReferenceData;
    selectedGrowthRateBasisType: GrowthRateBasisType;
    defaultGrowthRateBasisType: GrowthRateBasisType;
}) => {
    const { setValue, trigger } = IressForm.useFormContext();
    const { currentLow, currentMid, currentHigh } = useGrowthRateTableValues(
        selectedGrowthRateBasisType,
        defaultGrowthRateBasisType,
        growthRateLow,
        growthRateMid,
        growthRateHigh
    );
    const { atLeastOneGrowthRateGreaterThanZero } = useAtLeastOneGreaterThanZero(selectedGrowthRateBasisType);

    const rowBasis = useMemo(() => {
        const { inflationRatePercentage } = referenceData;
        if (selectedGrowthRateBasisType !== GrowthRateBasisType.FundSpecificInflation) {
            return null;
        }
        return [
            {
                growthRate: 'Low',
                defaultGrowthRate: String(currentLow),
                defaultCalculatedRate: calculateNominalFromInflationAdjustedRateFormatted(
                    currentLow!,
                    inflationRatePercentage
                ),
            },
            {
                growthRate: 'Mid',
                defaultGrowthRate: String(currentMid),
                defaultCalculatedRate: calculateNominalFromInflationAdjustedRateFormatted(
                    currentMid!,
                    inflationRatePercentage
                ),
            },
            {
                growthRate: 'High',
                defaultGrowthRate: String(currentHigh),
                defaultCalculatedRate: calculateNominalFromInflationAdjustedRateFormatted(
                    currentHigh!,
                    inflationRatePercentage
                ),
            },
        ];
    }, [currentHigh, currentLow, currentMid, referenceData, selectedGrowthRateBasisType]);
    const suffix = getSuffixFromGrowthRateBasisType(selectedGrowthRateBasisType);

    return useMemo(() => {
        if (rowBasis === null) {
            return null;
        }
        const { inflationRatePercentage } = referenceData;
        return rowBasis?.map(row => ({
            growthRate: row.growthRate,
            nominalRate: (
                <IressFormField
                    className="xps-table-field--justify-right-readonly"
                    label={`Nominal ${row.growthRate}`}
                    hiddenLabel
                    name={`projectedGrowthRates.calculatedRate${row.growthRate}${suffix}`}
                    defaultValue={row.defaultCalculatedRate}
                    render={controlledProps => (
                        <IressInput
                            readOnly
                            type={'number'}
                            {...controlledProps}
                            alignRight
                            append={<IressText className="xps-table-addon--remove-padding">%</IressText>}
                            data-testid={`calculatedRate${row.growthRate}`}
                        />
                    )}
                />
            ),
            inflationAdjustedRate: (
                <IressFormField
                    className="xps-table-field--justify-right"
                    label={`Inflation adjusted ${row.growthRate}`}
                    hiddenLabel
                    name={`projectedGrowthRates.growthRate${row.growthRate}${suffix}`}
                    defaultValue={row.defaultGrowthRate}
                    rules={{
                        required: `Inflation Adjusted - ${row.growthRate} is required`,
                        min: -20,
                        max: 20,
                        validate: (_, formValues) => {
                            return atLeastOneGrowthRateGreaterThanZero(formValues) || allRatesZeroErrorMessage;
                        },

                        pattern: twoDecimalPlaceRegex,
                    }}
                    onChange={async (event: FormEvent<HTMLInputElement>): Promise<void> => {
                        setValue(
                            `projectedGrowthRates.calculatedRate${row.growthRate}${suffix}`,
                            calculateNominalFromInflationAdjustedRateFormatted(
                                Number((event.target as HTMLInputElement).value),
                                inflationRatePercentage
                            )
                        );
                        await trigger(`projectedGrowthRates.growthRateLow${suffix}`);
                        await trigger(`projectedGrowthRates.growthRateMid${suffix}`);
                        await trigger(`projectedGrowthRates.growthRateHigh${suffix}`);
                    }}
                    render={controlledProps => (
                        <IressInput
                            {...controlledProps}
                            type={'number'}
                            alignRight
                            formatter={formatTwoDecimalPlaces}
                            append={<IressText className="xps-table-addon--remove-padding">%</IressText>}
                            width={'50perc'}
                        />
                    )}
                />
            ),
        }));
    }, [atLeastOneGrowthRateGreaterThanZero, referenceData, rowBasis, setValue, suffix, trigger]);
};
